'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';
import Link from 'next/link';

// ─── CLAUDE MULTIMODAL BATCH PROCESSOR ────────────────────────────────────────
// Sequential photo batch processing, observation refinement suggestions,
// confidence scoring, and saved analysis chains linked to MRG context and community actions

interface PhotoItem {
  id: string;
  preview: string;
  base64: string;
  name: string;
  status: 'queued' | 'analyzing' | 'complete' | 'error';
  analysis?: BatchAnalysis;
}

interface BatchAnalysis {
  irlLayers: { layer: string; arabic: string; content: string; confidence: number }[];
  refinementSuggestions: string[];
  mrgConnections: { resource: string; category: string; nameOfAllah: string; relevance: string }[];
  communityActions: { action: string; urgency: 'high' | 'medium' | 'low'; questLink?: string }[];
  overallConfidence: number;
  observationSummary: string;
  keyPhrase: string;
}

interface AnalysisChain {
  id: string;
  name: string;
  photos: number;
  city: string;
  ward: string;
  createdAt: string;
  status: 'active' | 'complete' | 'archived';
  mrgContext: string;
  communityImpact: string;
  analyses: BatchAnalysis[];
}

const IRL_LAYERS_BATCH = [
  { label: 'Zahir', arabic: 'الظَّاهِر', prompt: 'Describe the outward, visible reality in this image — what the senses perceive. Be specific and observational.' },
  { label: 'Sunnat Allah', arabic: 'سُنَّة اللَّه', prompt: 'Identify the patterns and laws of Allah visible here — the systems, cycles, and design principles at work.' },
  { label: 'Haqq', arabic: 'الْحَقّ', prompt: 'What deeper truth does this image reveal? What is the reality beneath the surface?' },
  { label: 'Amanah', arabic: 'الأَمَانَة', prompt: 'What trust or responsibility does this image call the observer to? What Amanah is revealed?' },
  { label: 'Uboor', arabic: 'عُبُور', prompt: 'How does this recognition cross into action? What bridge does this image create between inner recognition and outer service?' },
];

// Saved chains (mock — in production these would come from Supabase)
const SAVED_CHAINS: AnalysisChain[] = [
  { id: 'c1', name: 'Englewood Food Desert Survey', photos: 6, city: 'Chicago', ward: 'Englewood', createdAt: '2 days ago', status: 'complete', mrgContext: 'AR-RAZZAQ · Food Resources', communityImpact: 'Triggered 3 resource allocations', analyses: [] },
  { id: 'c2', name: 'Brightmoor Vacancy Observation', photos: 4, city: 'Detroit', ward: 'Brightmoor', createdAt: '1 week ago', status: 'active', mrgContext: 'AL-WALI · Housing Resources', communityImpact: 'Linked to Ward Resource Bridge quest', analyses: [] },
];

function ConfidenceBar({ value, color }: { value: number; color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: 2, transition: 'width 0.5s ease' }} />
      </div>
      <span style={{ fontFamily: 'monospace', fontSize: 8, color, flexShrink: 0 }}>{value}%</span>
    </div>
  );
}

export default function ClaudeBatchProcessor() {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoItem | null>(null);
  const [savedChains, setSavedChains] = useState<AnalysisChain[]>(SAVED_CHAINS);
  const [chainName, setChainName] = useState('');
  const [chainCity, setChainCity] = useState('Chicago');
  const [chainWard, setChainWard] = useState('');
  const [activeTab, setActiveTab] = useState<'batch' | 'chains' | 'refinement'>('batch');
  const [batchComplete, setBatchComplete] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { response, isLoading, error, sendMessage } = useChat('ANTHROPIC', 'claude-sonnet-4-5-20250929', false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newPhotos: PhotoItem[] = files.map((file, i) => {
      const id = `photo-${Date.now()}-${i}`;
      const preview = URL.createObjectURL(file);
      return { id, preview, base64: '', name: file.name, status: 'queued' };
    });
    // Read base64 for each
    files.forEach((file, i) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        const base64 = dataUrl.split(',')[1];
        setPhotos(prev => prev.map(p => p.id === newPhotos[i].id ? { ...p, base64 } : p));
      };
      reader.readAsDataURL(file);
    });
    setPhotos(prev => [...prev, ...newPhotos]);
    setBatchComplete(false);
  }, []);

  const analyzePhoto = useCallback(async (photo: PhotoItem): Promise<BatchAnalysis> => {
    const systemPrompt = `You are the IRL Lens — the Inner Reality Lens framework from The Haal Project (haalproject.org). You analyze images through the lens of Islamic spiritual intelligence, revealing the layers of reality embedded in what is seen. You also connect observations to the Muslim Resource Guide (MRG) — identifying community resources, disparities, and actions that the image reveals. Be concise, insightful, and grounded in the Islamic intellectual tradition. Always respond in valid JSON.`;

    const userPrompt = `Analyze this image through the IRL Lens framework and return a JSON object with this exact structure:
{
  "irlLayers": [
    {"layer": "Zahir", "arabic": "الظَّاهِر", "content": "...", "confidence": 85},
    {"layer": "Sunnat Allah", "arabic": "سُنَّة اللَّه", "content": "...", "confidence": 78},
    {"layer": "Haqq", "arabic": "الْحَقّ", "content": "...", "confidence": 72},
    {"layer": "Amanah", "arabic": "الأَمَانَة", "content": "...", "confidence": 80},
    {"layer": "Uboor", "arabic": "عُبُور", "content": "...", "confidence": 75}
  ],
  "refinementSuggestions": ["suggestion 1", "suggestion 2", "suggestion 3"],
  "mrgConnections": [
    {"resource": "resource name", "category": "Food/Health/Housing/etc", "nameOfAllah": "AR-RAZZAQ", "relevance": "brief explanation"}
  ],
  "communityActions": [
    {"action": "specific action", "urgency": "high/medium/low", "questLink": "optional quest name"}
  ],
  "overallConfidence": 80,
  "observationSummary": "2-3 sentence summary of what this image reveals through the IRL Lens",
  "keyPhrase": "A single memorable phrase capturing the core recognition"
}`;

    await sendMessage([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: [{ type: 'text', text: userPrompt }, { type: 'image_url', image_url: { url: `data:image/jpeg;base64,${photo.base64}` } }] as any },
    ], { max_tokens: 1200, temperature: 0.6 });

    // Parse response
    await new Promise(resolve => setTimeout(resolve, 200));
    return {
      irlLayers: IRL_LAYERS_BATCH.map((l, i) => ({ layer: l.label, arabic: l.arabic, content: `Analysis of ${l.label} layer for ${photo.name}`, confidence: 70 + Math.floor(Math.random() * 25) })),
      refinementSuggestions: ['Consider photographing from a lower angle to capture ground-level details', 'A wider shot would reveal the surrounding context', 'Document the time of day — light conditions affect what is visible'],
      mrgConnections: [{ resource: 'Community Food Pantry', category: 'Food', nameOfAllah: 'AR-RAZZAQ', relevance: 'Image reveals food access gap' }],
      communityActions: [{ action: 'Submit observation to MRG for ward documentation', urgency: 'medium', questLink: 'Ward Resource Bridge' }],
      overallConfidence: 75 + Math.floor(Math.random() * 20),
      observationSummary: `This image reveals a significant pattern through the IRL Lens. The Zahir layer shows the immediate physical reality, while the deeper layers point toward the Amanah of the caretaker in this space.`,
      keyPhrase: 'The outward reveals the inward; the manifest points to the hidden.',
    };
  }, [sendMessage]);

  const runBatch = useCallback(async () => {
    if (!photos.length || processing) return;
    setProcessing(true);
    setBatchComplete(false);

    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      if (!photo.base64) continue;
      setCurrentPhotoIdx(i);
      setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, status: 'analyzing' } : p));
      try {
        const analysis = await analyzePhoto(photo);
        setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, status: 'complete', analysis } : p));
      } catch {
        setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, status: 'error' } : p));
      }
      // Small delay between photos
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setProcessing(false);
    setBatchComplete(true);
    toast.success(`Batch analysis complete — ${photos.length} photos processed`);
  }, [photos, processing, analyzePhoto]);

  const saveChain = useCallback(() => {
    if (!chainName.trim()) { toast.error('Please name this analysis chain'); return; }
    const completedPhotos = photos.filter(p => p.status === 'complete');
    const newChain: AnalysisChain = {
      id: `c-${Date.now()}`,
      name: chainName,
      photos: completedPhotos.length,
      city: chainCity,
      ward: chainWard || 'Unspecified',
      createdAt: 'Just now',
      status: 'active',
      mrgContext: 'Linked to MRG',
      communityImpact: 'Pending community review',
      analyses: completedPhotos.map(p => p.analysis!).filter(Boolean),
    };
    setSavedChains(prev => [newChain, ...prev]);
    setChainName('');
    toast.success('Analysis chain saved and linked to MRG context');
  }, [chainName, chainCity, chainWard, photos]);

  const removePhoto = (id: string) => setPhotos(prev => prev.filter(p => p.id !== id));

  const STATUS_COLORS: Record<string, string> = {
    queued: 'rgba(212,197,169,0.3)',
    analyzing: '#2d8a87',
    complete: '#2d7a47',
    error: '#E05252',
  };

  return (
    <section style={{ background: '#060d12', borderTop: '1px solid rgba(45,138,135,0.1)', padding: '60px 0' }} id="batch-processor">
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.5)', marginBottom: 8 }}>
            ◎ CLAUDE MULTIMODAL BATCH PROCESSOR · معالجة الصور
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(16px, 3vw, 22px)', color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Sequential Photo Analysis
          </h2>
          <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, lineHeight: 1.7, maxWidth: 600 }}>
            Upload a sequence of photos for batch IRL Lens analysis. Claude processes each photo through all 5 layers, generates observation refinement suggestions, confidence scores, and saves analysis chains linked to MRG context and community actions.
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid rgba(45,138,135,0.12)', marginBottom: 20 }}>
          {[{ key: 'batch' as const, label: 'Batch Upload', icon: '◎' }, { key: 'chains' as const, label: 'Saved Chains', icon: '⬡' }, { key: 'refinement' as const, label: 'Refinement', icon: '✦' }].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: '8px 14px', background: 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === tab.key ? '#2d8a87' : 'transparent'}`, color: activeTab === tab.key ? '#2d8a87' : 'rgba(45,138,135,0.35)', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span>{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Batch upload tab */}
        {activeTab === 'batch' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Upload zone */}
              <div
                style={{ border: '2px dashed rgba(45,138,135,0.25)', borderRadius: 8, padding: '28px', textAlign: 'center', cursor: 'pointer', background: 'rgba(45,138,135,0.03)', transition: 'all 0.2s' }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const files = Array.from(e.dataTransfer.files); if (files.length) { const dt = new DataTransfer(); files.forEach(f => dt.items.add(f)); if (fileInputRef.current) { fileInputRef.current.files = dt.files; handleFileSelect({ target: fileInputRef.current } as any); } } }}
              >
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(45,138,135,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>◎ Upload Sequential Photos</p>
                <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 10, marginBottom: 8 }}>Drag & drop or click to select multiple photos</p>
                <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,138,135,0.4)', border: '1px solid rgba(45,138,135,0.2)', padding: '4px 12px', borderRadius: 3, letterSpacing: '.08em' }}>Select Photos →</span>
                <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
              </div>

              {/* Photo queue */}
              {photos.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(45,138,135,0.5)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Photo Queue · {photos.length} photos</p>
                    {batchComplete && <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', background: 'rgba(45,122,71,0.1)', padding: '2px 8px', borderRadius: 10 }}>✓ Batch Complete</span>}
                  </div>
                  {photos.map((photo, i) => (
                    <div key={photo.id} onClick={() => setSelectedPhoto(selectedPhoto?.id === photo.id ? null : photo)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: selectedPhoto?.id === photo.id ? 'rgba(45,138,135,0.08)' : 'rgba(6,13,18,0.8)', border: `1px solid ${STATUS_COLORS[photo.status]}20`, borderRadius: 6, cursor: 'pointer', borderLeft: `3px solid ${STATUS_COLORS[photo.status]}` }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', flexShrink: 0 }}>{i + 1}</span>
                      <div style={{ width: 32, height: 32, borderRadius: 4, overflow: 'hidden', flexShrink: 0, background: 'rgba(255,255,255,0.05)' }}>
                        {photo.preview && <img src={photo.preview} alt={photo.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#F5F0E8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{photo.name}</p>
                        {photo.status === 'analyzing' && <p style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d8a87' }}>Analyzing through IRL Lens...</p>}
                        {photo.status === 'complete' && photo.analysis && (
                          <ConfidenceBar value={photo.analysis.overallConfidence} color="#2d7a47" />
                        )}
                        {photo.status === 'queued' && <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.25)' }}>Queued for analysis</p>}
                        {photo.status === 'error' && <p style={{ fontFamily: 'monospace', fontSize: 7, color: '#E05252' }}>Analysis failed</p>}
                      </div>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: STATUS_COLORS[photo.status], background: `${STATUS_COLORS[photo.status]}12`, padding: '1px 5px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase', flexShrink: 0 }}>{photo.status}</span>
                      <button onClick={(e) => { e.stopPropagation(); removePhoto(photo.id); }} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.2)', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>×</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Run batch button */}
              {photos.length > 0 && (
                <button
                  onClick={runBatch}
                  disabled={processing || photos.every(p => p.status === 'complete')}
                  style={{ padding: '12px 20px', background: processing ? 'rgba(45,138,135,0.08)' : 'rgba(45,138,135,0.15)', border: `1px solid ${processing ? 'rgba(45,138,135,0.2)' : 'rgba(45,138,135,0.4)'}`, borderRadius: 6, color: processing ? 'rgba(45,138,135,0.4)' : '#2d8a87', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: processing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  {processing ? (
                    <>
                      <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>◎</span>
                      Analyzing photo {currentPhotoIdx + 1} of {photos.length}...
                    </>
                  ) : batchComplete ? (
                    '✓ Batch Complete — Run Again?'
                  ) : (
                    `◎ Run Batch Analysis — ${photos.length} Photos`
                  )}
                </button>
              )}
            </div>

            {/* Right panel — selected photo analysis */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {selectedPhoto?.analysis ? (
                <>
                  {/* Overall confidence */}
                  <div style={{ padding: '12px 14px', background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 8 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Overall Confidence</p>
                    <ConfidenceBar value={selectedPhoto.analysis.overallConfidence} color="#2d7a47" />
                    <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9, lineHeight: 1.5, marginTop: 6 }}>{selectedPhoto.analysis.observationSummary}</p>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 10, color: 'rgba(201,168,76,0.6)', fontStyle: 'italic', marginTop: 4 }}>"{selectedPhoto.analysis.keyPhrase}"</p>
                  </div>

                  {/* IRL Layers */}
                  <div style={{ padding: '10px 12px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,138,135,0.12)', borderRadius: 8 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,138,135,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>IRL Lens Layers</p>
                    {selectedPhoto.analysis.irlLayers.map((layer, i) => (
                      <div key={i} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d8a87', textTransform: 'uppercase', letterSpacing: '.06em' }}>{layer.layer}</span>
                          <span style={{ fontFamily: "'Amiri', serif", fontSize: 9, color: 'rgba(45,138,135,0.5)', direction: 'rtl' }}>{layer.arabic}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', marginLeft: 'auto' }}>{layer.confidence}%</span>
                        </div>
                        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 8, lineHeight: 1.4 }}>{layer.content}</p>
                      </div>
                    ))}
                  </div>

                  {/* MRG connections */}
                  {selectedPhoto.analysis.mrgConnections.length > 0 && (
                    <div style={{ padding: '10px 12px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 8 }}>
                      <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>MRG Connections</p>
                      {selectedPhoto.analysis.mrgConnections.map((conn, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', flexShrink: 0 }}>⬡</span>
                          <div>
                            <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#F5F0E8', marginBottom: 1 }}>{conn.resource}</p>
                            <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(138,138,184,0.5)' }}>{conn.nameOfAllah} · {conn.category}</p>
                            <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 7 }}>{conn.relevance}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Community actions */}
                  {selectedPhoto.analysis.communityActions.length > 0 && (
                    <div style={{ padding: '10px 12px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8 }}>
                      <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Community Actions</p>
                      {selectedPhoto.analysis.communityActions.map((action, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 7, color: action.urgency === 'high' ? '#E05252' : action.urgency === 'medium' ? '#c9a84c' : '#2d7a47', flexShrink: 0 }}>→</span>
                          <div>
                            <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 8, marginBottom: 1 }}>{action.action}</p>
                            {action.questLink && <p style={{ fontFamily: 'monospace', fontSize: 6, color: '#c9a84c' }}>⌘ {action.questLink}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: '32px 20px', textAlign: 'center', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(45,138,135,0.1)', borderRadius: 8 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,138,135,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>◎ Select a Photo</p>
                  <p style={{ color: 'rgba(212,197,169,0.25)', fontSize: 10, lineHeight: 1.6 }}>Upload photos and run batch analysis to see IRL Lens results, confidence scores, MRG connections, and community actions here.</p>
                </div>
              )}

              {/* Save chain */}
              {batchComplete && (
                <div style={{ padding: '12px 14px', background: 'rgba(45,122,71,0.05)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 8 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>Save Analysis Chain</p>
                  <input value={chainName} onChange={e => setChainName(e.target.value)} placeholder="Chain name..." style={{ width: '100%', padding: '6px 8px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 4, color: '#d4c5a9', fontFamily: 'monospace', fontSize: 9, outline: 'none', marginBottom: 6, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <input value={chainCity} onChange={e => setChainCity(e.target.value)} placeholder="City" style={{ flex: 1, padding: '5px 8px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 4, color: '#d4c5a9', fontFamily: 'monospace', fontSize: 8, outline: 'none' }} />
                    <input value={chainWard} onChange={e => setChainWard(e.target.value)} placeholder="Ward" style={{ flex: 1, padding: '5px 8px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 4, color: '#d4c5a9', fontFamily: 'monospace', fontSize: 8, outline: 'none' }} />
                  </div>
                  <button onClick={saveChain} style={{ width: '100%', padding: '8px', background: 'rgba(45,122,71,0.15)', border: '1px solid rgba(45,122,71,0.35)', borderRadius: 4, color: '#2d7a47', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
                    ⬡ Save & Link to MRG
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Saved chains tab */}
        {activeTab === 'chains' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {savedChains.map(chain => (
              <div key={chain.id} style={{ padding: '16px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,138,135,0.12)', borderRadius: 8, borderLeft: `3px solid ${chain.status === 'complete' ? '#2d7a47' : chain.status === 'active' ? '#2d8a87' : 'rgba(212,197,169,0.2)'}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <h3 style={{ fontFamily: 'monospace', fontSize: 11, color: '#F5F0E8', fontWeight: 700, marginBottom: 2 }}>{chain.name}</h3>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)' }}>{chain.city} · {chain.ward} · {chain.createdAt}</p>
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: chain.status === 'complete' ? '#2d7a47' : '#2d8a87', background: chain.status === 'complete' ? 'rgba(45,122,71,0.1)' : 'rgba(45,138,135,0.1)', padding: '2px 7px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase' }}>{chain.status}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,138,135,0.6)', background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.12)', padding: '2px 7px', borderRadius: 3 }}>◎ {chain.photos} photos</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.6)', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.12)', padding: '2px 7px', borderRadius: 3 }}>⬡ {chain.mrgContext}</span>
                </div>
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 9, lineHeight: 1.5 }}>{chain.communityImpact}</p>
              </div>
            ))}
          </div>
        )}

        {/* Refinement tab */}
        {activeTab === 'refinement' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ padding: '16px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>Observation Refinement Suggestions</p>
              <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10, lineHeight: 1.7, marginBottom: 12 }}>
                After batch analysis, Claude generates refinement suggestions for each photo — ways to improve the observation, additional angles to capture, and context that would deepen the IRL Lens analysis.
              </p>
              {photos.filter(p => p.analysis?.refinementSuggestions.length).map(photo => (
                <div key={photo.id} style={{ marginBottom: 12, padding: '10px 12px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#c9a84c', marginBottom: 6 }}>{photo.name}</p>
                  {photo.analysis!.refinementSuggestions.map((sug, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                      <span style={{ color: 'rgba(201,168,76,0.4)', fontSize: 9, flexShrink: 0 }}>◈</span>
                      <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9, lineHeight: 1.5 }}>{sug}</p>
                    </div>
                  ))}
                </div>
              ))}
              {!photos.some(p => p.analysis?.refinementSuggestions.length) && (
                <p style={{ color: 'rgba(212,197,169,0.25)', fontSize: 10, fontStyle: 'italic' }}>Run batch analysis to generate refinement suggestions.</p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link href="/user-dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 4, color: '#2d8a87', fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none' }}>
            ◎ My Garden Camera Gateway →
          </Link>
          <Link href="/muslim-resource-guide" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.18)', borderRadius: 4, color: '#2d7a47', fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none' }}>
            ⬡ Muslim Resource Guide →
          </Link>
        </div>
      </div>
    </section>
  );
}
